/**
 * Family-scoped load hook
 *
 * Runs the given load function on mount AND re-runs it whenever the active
 * family changes (family switch, join, leave). Pages previously loaded only on
 * mount via `useEffect(..., [load])`, so switching families while a page was
 * mounted left stale data from the previous family on screen.
 *
 * The re-load is deferred with `queueMicrotask` because the family stores
 * notify the active-family listeners immediately BEFORE calling
 * `resetAllFamilyScopedStores()` — loading synchronously would race the reset
 * and get wiped. `familyId === null` notifications (logout / left last family)
 * are skipped: the store resets already emptied the data and the services
 * would throw without an active family.
 *
 * @module shared/hooks/useFamilyScopedLoad
 */

import { useEffect, useRef } from 'react';
import { subscribeActiveFamilyChange } from '@/shared/lib/familyContext';

export function useFamilyScopedLoad(load: () => Promise<void>): void {
  // Latest-callback pattern: don't resubscribe when the store recreates the
  // load function reference.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    void loadRef.current();

    const unsubscribe = subscribeActiveFamilyChange((familyId) => {
      if (familyId === null) {
        return;
      }
      // Defer past the synchronous store resets that follow the notification.
      queueMicrotask(() => {
        void loadRef.current();
      });
    });

    return unsubscribe;
  }, []);
}
